import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axiosInstance from "../../config/axiosApi/axiosApi.jsx";
import { useDispatch } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import Preloader from "../../components/CustomComponents/preLoad/preLoader.js";
import showErrorToast, { showSuccessToast } from "../../components/CustomComponents/toast/toast.js";
import APIs from "../../Constants/apis.js";
import swal from "sweetalert";
import Pagination from "../../components/CustomComponents/Pagination/pagination.js";
import { logout } from "../../app/auth/authSlice.js";
import useThemeColor from "../../hooks/useThemeColor.js";
import useDarkMode from "../../hooks/useDarkMode.js";
import AddUpdateCampaignModal from "./addUpdateCampaignModal.jsx";
import "../compnay/ManageCompany.css";
import "./manageMarketplaceCampaigns.css";

const typeLabel = (type) => {
  const map = {
    PercentageOff: "Percentage off",
    FromPrice: "From price",
    CategoryDeal: "Category deal",
    FreeDelivery: "Free delivery",
  };
  return map[type] || type || "—";
};

const enrollmentLabel = (type) => {
  const map = {
    Manual: "Manual",
    OptIn: "Opt-in",
    AutoRule: "Auto rule",
    Mixed: "Mixed",
  };
  return map[type] || type || "—";
};

const formatCampaignDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const ManageMarketplaceCampaigns = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const themeColor = useThemeColor();
  const isDarkMode = useDarkMode();

  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [pageSize] = useState(50);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [pageNumber, setPageNumber] = useState(1);
  const [campaigns, setCampaigns] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [categories, setCategories] = useState([]);
  const [pendingOptIns, setPendingOptIns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [campaignToEdit, setCampaignToEdit] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const dragItem = useRef(null);
  const dragOverItem = useRef(null);

  const fetchCampaigns = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.GET_ALL_MARKETPLACE_CAMPAIGNS, {
        params: {
          Search: search.trim() || "",
          SortBy: "Display_Order",
          SortOrder: "ASC",
          PageSize: pageSize,
          PageNumber: pageNumber,
        },
      });
      if (response.data.status) {
        setCampaigns(Array.isArray(response.data.data) ? response.data.data : []);
        setTotalRecords(response.data.totalRecords || 0);
        setTotalPages(response.data.totalPages || 1);
      } else {
        showErrorToast(response.data.message || response.data.Message || "Failed to fetch campaigns");
      }
    } catch (err) {
      if (err.response?.status === 401) {
        dispatch(logout());
        setTimeout(() => navigate("/user/login", { replace: true }), 1000);
      } else {
        showErrorToast("Failed to fetch campaigns");
      }
    } finally {
      setLoading(false);
    }
  }, [search, pageSize, pageNumber, dispatch, navigate]);

  const fetchLookups = useCallback(async () => {
    try {
      const [companiesRes, categoriesRes] = await Promise.all([
        axiosInstance.get(APIs.GET_ALL_COMPANIES, {
          params: { PageSize: 500, PageNumber: 1, SortBy: "CompName", SortOrder: "ASC" },
        }),
        axiosInstance.get(APIs.GET_ALL_RESTURANT_CATEGORIES, { params: { PageSize: 200, PageNumber: 1 } }),
      ]);

      const payload = companiesRes.data?.data;
      const companyList = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.companies)
          ? payload.companies
          : Array.isArray(payload?.Companies)
            ? payload.Companies
            : [];

      const isMarketplaceCompany = (c) =>
        Boolean(
          c?.isOnlineMarketplaceEnabled ??
            c?.IsOnlineMarketplaceEnabled ??
            c?.isWebApp ??
            c?.ISWebApp
        );

      setCompanies(companyList.filter(isMarketplaceCompany));

      const categoryPayload = categoriesRes.data?.data;
      const categoryList = Array.isArray(categoryPayload)
        ? categoryPayload
        : Array.isArray(categoryPayload?.categories)
          ? categoryPayload.categories
          : Array.isArray(categoryPayload?.data)
            ? categoryPayload.data
            : [];
      setCategories(categoryList);
    } catch {
      setCompanies([]);
      setCategories([]);
    }
  }, []);

  const fetchPendingOptIns = useCallback(async (campaignId) => {
    if (!campaignId) {
      setPendingOptIns([]);
      return;
    }
    try {
      const response = await axiosInstance.get(APIs.GET_PENDING_CAMPAIGN_OPT_INS, {
        params: { Campaign_ID: campaignId, PageNumber: 1, PageSize: 100 },
      });
      if (response.data.status) {
        setPendingOptIns(Array.isArray(response.data.data) ? response.data.data : []);
      }
    } catch {
      setPendingOptIns([]);
    }
  }, []);

  useEffect(() => {
    fetchCampaigns();
  }, [fetchCampaigns]);

  useEffect(() => {
    fetchLookups();
  }, [fetchLookups]);

  const stats = useMemo(() => {
    const active = campaigns.filter((c) => c.isActive).length;
    const restaurants = campaigns.reduce((sum, c) => sum + Number(c.activeRestaurantCount ?? 0), 0);
    const pending = campaigns.reduce((sum, c) => sum + Number(c.pendingOptInCount ?? 0), 0);
    return {
      total: totalRecords || campaigns.length,
      active,
      restaurants,
      pending,
    };
  }, [campaigns, totalRecords]);

  const openEdit = async (campaign) => {
    try {
      setLoading(true);
      const id = campaign.campaign_ID || campaign.id;
      const response = await axiosInstance.get(APIs.GET_MARKETPLACE_CAMPAIGN_BY_ID, { params: { ID: id } });
      if (response.data.status) {
        setCampaignToEdit(response.data.data);
        await fetchPendingOptIns(id);
        setShowEditModal(true);
      }
    } catch {
      showErrorToast("Failed to load campaign");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    const result = await swal({ title: "Delete campaign?", icon: "warning", buttons: true, dangerMode: true });
    if (!result) return;
    try {
      setLoading(true);
      const response = await axiosInstance.delete(APIs.DELETE_MARKETPLACE_CAMPAIGN, { params: { ID: id } });
      if (response.data.status) {
        showSuccessToast("Campaign deleted");
        fetchCampaigns();
      } else {
        showErrorToast(response.data.message || response.data.Message || "Delete failed");
      }
    } catch {
      showErrorToast("Delete failed");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (payload, isEdit) => {
    try {
      setLoading(true);
      const response = isEdit
        ? await axiosInstance.put(APIs.PUT_UPDATE_MARKETPLACE_CAMPAIGN, payload)
        : await axiosInstance.post(APIs.POST_INSERT_MARKETPLACE_CAMPAIGN, payload);
      if (response.data.status) {
        showSuccessToast(isEdit ? "Campaign updated" : "Campaign created");
        setShowAddModal(false);
        setShowEditModal(false);
        setCampaignToEdit(null);
        fetchCampaigns();
      } else {
        showErrorToast(response.data.message || response.data.Message || "Save failed");
      }
    } catch (err) {
      showErrorToast(err.response?.data?.message || err.response?.data?.Message || "Save failed");
    } finally {
      setLoading(false);
    }
  };

  const handleApproveOptIn = async (id) => {
    const response = await axiosInstance.post(APIs.POST_APPROVE_CAMPAIGN_OPT_IN, null, { params: { ID: id } });
    if (response.data.status) {
      showSuccessToast("Opt-in approved");
      fetchPendingOptIns(campaignToEdit?.campaign_ID || campaignToEdit?.id);
      fetchCampaigns();
    } else {
      showErrorToast("Approve failed");
    }
  };

  const handleRejectOptIn = async (id) => {
    const response = await axiosInstance.post(APIs.POST_REJECT_CAMPAIGN_OPT_IN, null, { params: { ID: id } });
    if (response.data.status) {
      showSuccessToast("Opt-in rejected");
      fetchPendingOptIns(campaignToEdit?.campaign_ID || campaignToEdit?.id);
    } else {
      showErrorToast("Reject failed");
    }
  };

  const applySearch = () => {
    setPageNumber(1);
    setSearch(searchInput.trim());
  };

  const handleDragStart = (e, index) => {
    dragItem.current = index;
    e.dataTransfer.effectAllowed = "move";
    e.currentTarget.classList.add("is-dragging");
  };

  const handleDragEnter = (e, index) => {
    e.preventDefault();
    dragOverItem.current = index;
    setDragOverIndex(index);
  };

  const handleDragLeave = (e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragOverIndex(null);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    const fromIndex = dragItem.current;
    const toIndex = dragOverItem.current;
    setDragOverIndex(null);

    if (fromIndex == null || toIndex == null || fromIndex === toIndex) {
      dragItem.current = null;
      dragOverItem.current = null;
      return;
    }

    const previous = [...campaigns];
    const next = [...campaigns];
    const [moved] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, moved);
    setCampaigns(next);

    try {
      const updates = next.map((item, index) => ({
        Campaign_ID: item.campaign_ID || item.id,
        Display_Order: (pageNumber - 1) * pageSize + index + 1,
      }));
      const response = await axiosInstance.post(APIs.POST_UPDATE_MARKETPLACE_CAMPAIGN_DISPLAY_ORDER, {
        Updates: updates,
      });
      if (response.data.status) {
        showSuccessToast("Display order updated");
      } else {
        showErrorToast(response.data.message || response.data.Message || "Failed to update order");
        setCampaigns(previous);
      }
    } catch {
      showErrorToast("Failed to update order");
      setCampaigns(previous);
    } finally {
      dragItem.current = null;
      dragOverItem.current = null;
    }
  };

  const handleDragEnd = (e) => {
    e.currentTarget.classList.remove("is-dragging");
    setDragOverIndex(null);
    dragItem.current = null;
    dragOverItem.current = null;
  };

  const primaryBtn = {
    background: themeColor,
    boxShadow: `0 1px 3px ${themeColor}55`,
  };

  return (
    <div className={`mc-page manage-company-page ${isDarkMode ? "is-dark" : "is-light"}`}>
      {loading && <Preloader />}

      <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-3">
        <div>
          <ol className="breadcrumb mb-1">
            <li className="breadcrumb-item">
              <Link to="/dashboard">Dashboard</Link>
            </li>
            <li className="breadcrumb-item active">Go Fatafat Campaigns</li>
          </ol>
          <h1 className="page-header mb-1">Go Fatafat Campaigns</h1>
          <p className="mc-desc">
            Create marketplace offers, control enrollment, and approve restaurant opt-ins.
          </p>
        </div>
        <button type="button" className="mc-btn-primary" style={primaryBtn} onClick={() => setShowAddModal(true)}>
          <i className="fa fa-plus" />
          Add Campaign
        </button>
      </div>

      <div className="mc-toolbar">
        <div className="mc-field">
          <label htmlFor="mc-search">Search</label>
          <input
            id="mc-search"
            type="text"
            placeholder="Search by name, headline, slug…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") applySearch();
            }}
          />
        </div>
        <div className="mc-toolbar-actions">
          <button
            type="button"
            className="mc-btn-ghost"
            onClick={() => {
              setSearchInput("");
              setSearch("");
              setPageNumber(1);
            }}
          >
            Reset
          </button>
          <button type="button" className="mc-btn-ghost" onClick={fetchCampaigns}>
            Refresh
          </button>
          <button type="button" className="mc-btn-primary" style={primaryBtn} onClick={applySearch}>
            Apply
          </button>
        </div>
      </div>

      <div className="mc-kpi-row">
        <div className="mc-kpi">
          <div className="mc-kpi-label">Campaigns</div>
          <div className="mc-kpi-value">{stats.total}</div>
        </div>
        <div className="mc-kpi">
          <div className="mc-kpi-label">Active</div>
          <div className="mc-kpi-value is-ok">{stats.active}</div>
        </div>
        <div className="mc-kpi">
          <div className="mc-kpi-label">Restaurants enrolled</div>
          <div className="mc-kpi-value">{stats.restaurants}</div>
        </div>
        <div className="mc-kpi">
          <div className="mc-kpi-label">Pending opt-ins</div>
          <div className={`mc-kpi-value ${stats.pending > 0 ? "is-warn" : ""}`}>{stats.pending}</div>
        </div>
      </div>

      <div className="mc-panel">
        <div className="mc-panel-head">
          <h2 className="mc-panel-title">Campaigns</h2>
          <span className="mc-panel-meta">
            Showing {campaigns.length} of {totalRecords} campaigns · Drag rows to reorder
          </span>
        </div>
        <div className="table-responsive">
          <table className="mc-table">
            <thead>
              <tr>
                <th className="mc-drag-col" aria-label="Reorder" />
                <th>Name</th>
                <th>Type</th>
                <th>Enrollment</th>
                <th>Start</th>
                <th>End</th>
                <th className="text-center">Restaurants</th>
                <th className="text-center">Pending</th>
                <th className="text-center">Status</th>
                <th className="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {campaigns.length === 0 ? (
                <tr>
                  <td colSpan={10}>
                    <div className="mc-empty">
                      <i className="fa fa-bullhorn" />
                      No campaigns found. Create one to promote offers on Go Fatafat.
                    </div>
                  </td>
                </tr>
              ) : (
                campaigns.map((item, index) => {
                  const id = item.campaign_ID || item.id;
                  const name = item.campaign_Name || item.name;
                  const headline = item.headline;
                  const type = item.campaign_Type || item.campaignType;
                  const enrollment = item.enrollment_Type || item.enrollmentType;
                  const pending = Number(item.pendingOptInCount ?? 0);
                  return (
                    <tr
                      key={id}
                      draggable
                      className={dragOverIndex === index ? "is-drag-over" : undefined}
                      onDragStart={(e) => handleDragStart(e, index)}
                      onDragEnter={(e) => handleDragEnter(e, index)}
                      onDragLeave={handleDragLeave}
                      onDragOver={handleDragOver}
                      onDrop={handleDrop}
                      onDragEnd={handleDragEnd}
                    >
                      <td className="mc-drag-col">
                        <span className="mc-drag-handle" title="Drag to reorder" aria-hidden="true">
                          <i className="fa fa-bars" />
                        </span>
                      </td>
                      <td>
                        <span className="mc-name">{name}</span>
                        {headline ? <span className="mc-sub">{headline}</span> : null}
                      </td>
                      <td>
                        <span className="mc-type-pill">{typeLabel(type)}</span>
                      </td>
                      <td>{enrollmentLabel(enrollment)}</td>
                      <td>
                        <span className="mc-date">{formatCampaignDate(item.startDate)}</span>
                      </td>
                      <td>
                        <span className="mc-date">{formatCampaignDate(item.endDate)}</span>
                      </td>
                      <td className="mc-num">{item.activeRestaurantCount ?? 0}</td>
                      <td className="mc-num">{pending}</td>
                      <td className="text-center">
                        <span className={`mc-status ${item.isActive ? "is-active" : "is-inactive"}`}>
                          {item.isActive ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td>
                        <div className="mc-actions">
                          <button
                            type="button"
                            className="mc-icon-btn"
                            title="Edit"
                            draggable={false}
                            onDragStart={(e) => e.stopPropagation()}
                            onClick={() => openEdit(item)}
                          >
                            <i className="fa fa-edit" />
                          </button>
                          <button
                            type="button"
                            className="mc-icon-btn is-danger"
                            title="Delete"
                            draggable={false}
                            onDragStart={(e) => e.stopPropagation()}
                            onClick={() => handleDelete(id)}
                          >
                            <i className="fa fa-trash" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        {totalPages > 1 ? (
          <div className="p-3">
            <Pagination currentPage={pageNumber} totalPages={totalPages} onPageChange={setPageNumber} />
          </div>
        ) : null}
      </div>

      <AddUpdateCampaignModal
        show={showAddModal}
        onHide={() => setShowAddModal(false)}
        onSubmit={handleSubmit}
        isEdit={false}
        campaign={null}
        companies={companies}
        categories={categories}
        pendingOptIns={[]}
        onApproveOptIn={handleApproveOptIn}
        onRejectOptIn={handleRejectOptIn}
      />
      <AddUpdateCampaignModal
        show={showEditModal}
        onHide={() => {
          setShowEditModal(false);
          setCampaignToEdit(null);
          setPendingOptIns([]);
        }}
        onSubmit={handleSubmit}
        isEdit={true}
        campaign={campaignToEdit}
        companies={companies}
        categories={categories}
        pendingOptIns={pendingOptIns}
        onApproveOptIn={handleApproveOptIn}
        onRejectOptIn={handleRejectOptIn}
      />
    </div>
  );
};

export default ManageMarketplaceCampaigns;
